import React, { useEffect, useState } from 'react';
import { useController } from 'react-hook-form';
import cronstrue from 'cronstrue';
import Input from '@amzn/meridian/input';
import { type TranslateFn, useTranslation } from 'src/arb-wrapper';
import type { FormMeridianInputProps, FormRhfControllerProps } from 'src/components/common/forms/types';

const parseCronExpression = (value: string, t: TranslateFn) => {
  // cronsrue allows 5, 6 and 7 part expressions, but we only support 5. We
  // only catch our unsupported cases cronstrue would normally allow and let it
  // handle other errors naturally.
  const cronPartsCount = value ? value.trim().split(/\s/).length : 0;
  if (cronPartsCount > 5 && cronPartsCount <= 7) {
    return {
      errorMessage: t('form_cron_input_parts_count_error', { cronPartsCount }),
    };
  }

  try {
    const parsedExpression = cronstrue.toString(value);
    return { parsedExpression };
  } catch (err) {
    return { errorMessage: err as string };
  }
};

interface FormCronInputProps
  extends Omit<FormMeridianInputProps, 'type'>,
  FormRhfControllerProps {
  value?: string;
}

/**
 * `FormCronInput` is a Meridian `Input` supporting form validation. It will
 * present a human friendly description of the cron expression entered.
 */
export const FormCronInput: React.FC<FormCronInputProps> = (props) => {
  const { control, name, ...meridianProps } = props;

  const { t } = useTranslation();
  const [helperText, setHelperText] = useState('');
  const { field, fieldState } = useController({
    control,
    name,
    rules: {
      validate: {
        isValidCronExpression: (value: string) =>
          parseCronExpression(value, t).errorMessage,
      },
    },
  });

  useEffect(() => {
    const cron = field.value as string;
    const parsedExpression =
      parseCronExpression(cron, t).parsedExpression ?? '';
    setHelperText(`${t('form_cron_input_parsed_prefix')} ${parsedExpression}`);
  }, [field.value, setHelperText, t]);

  return (
    <Input
      {...meridianProps}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
      helperText={helperText}
      value={field.value}
      onBlur={field.onBlur}
      onChange={field.onChange}
    />
  );
};
